import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../api/apiClient";
import { NewsletterResponse, NewsletterSubscriber } from "../interfaces/Newsletter";

interface NewsletterState {
  loading: boolean;
  subscribing: boolean;
  error: string | null;
  data: NewsletterResponse | null;
  exportData: NewsletterSubscriber[] | null;
}

const initialState: NewsletterState = {
  loading: false,
  subscribing: false,
  error: null,
  data: null,
  exportData: null,
};

// Subscribe (Public)
export const subscribeNewsletter = createAsyncThunk(
  "newsletter/subscribe",
  async (payload: string | { email: string; website?: string }, thunkAPI) => {
    try {
      const data = typeof payload === "string" ? { email: payload } : payload;
      const response = await apiClient.post("/api/v1/newsletter/subscribe", data);
      return response.data;
    } catch (error: any) {
      const message =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to subscribe";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Fetch paginated subscribers (Admin)
export const fetchNewsletterSubscribers = createAsyncThunk(
  "newsletter/fetchSubscribers",
  async (
    params: { page?: number; limit?: number; search?: string } = {},
    thunkAPI
  ) => {
    try {
      const { page = 1, limit = 10, search = "" } = params;
      const response = await apiClient.get("/api/v1/newsletter", {
        params: { page, limit, search },
      });
      return response.data.data as NewsletterResponse;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || "Failed to fetch subscribers";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Export all subscribers (Admin)
export const exportAllNewsletterSubscribers = createAsyncThunk(
  "newsletter/exportAll",
  async (_, thunkAPI) => {
    try {
      const response = await apiClient.get("/api/v1/newsletter/export");
      return response.data.data.subscribers as NewsletterSubscriber[];
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || "Failed to export subscribers";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Delete subscriber (Admin)
export const deleteNewsletterSubscriber = createAsyncThunk(
  "newsletter/deleteSubscriber",
  async (id: string, thunkAPI) => {
    try {
      await apiClient.delete(`/api/v1/newsletter/${id}`);
      return id;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || "Failed to delete subscriber";
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const newsletterSlice = createSlice({
  name: "newsletter",
  initialState,
  reducers: {
    resetNewsletterState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Subscribe
      .addCase(subscribeNewsletter.pending, (state) => {
        state.subscribing = true;
        state.error = null;
      })
      .addCase(subscribeNewsletter.fulfilled, (state) => {
        state.subscribing = false;
      })
      .addCase(subscribeNewsletter.rejected, (state, action) => {
        state.subscribing = false;
        state.error = action.payload as string;
      })
      // Fetch Subscribers
      .addCase(fetchNewsletterSubscribers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNewsletterSubscribers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchNewsletterSubscribers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete Subscriber
      .addCase(deleteNewsletterSubscriber.fulfilled, (state, action) => {
        if (state.data?.subscribers) {
          state.data.subscribers = state.data.subscribers.filter(
            (s) => s.id !== action.payload
          );
          state.data.total = Math.max(0, state.data.total - 1);
        }
      });
  },
});

export const { resetNewsletterState } = newsletterSlice.actions;
export default newsletterSlice.reducer;
